import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, X, Plus, Minus, Trash2, Lock, ArrowLeft, Package, CreditCard, Banknote, Building2, Check, Ticket, Clock, LogIn, User, LogOut, ExternalLink, Globe } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Toaster, toast } from "sonner";
import { AuthModal } from "./components/AuthModal";
import { ProfileMenu } from "./components/ProfileMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

export default function Shop() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [customName, setCustomName] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Auth state
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Order deadline state
  const [orderDeadline, setOrderDeadline] = useState(null);
  const [deadlineEnabled, setDeadlineEnabled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [deadlineStatus, setDeadlineStatus] = useState("active"); // active, grace_period, closed
  
  // Checkout steps: 1 = details, 2 = payment, 3 = confirm
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Checkout form
  const [checkoutForm, setCheckoutForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    notes: "",
    payment_method: "cash",
    subscribe_newsletter: false,
    coupon_code: ""
  });

  // Coupon state
  const [couponValidation, setCouponValidation] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchShopSettings();
    const savedCart = localStorage.getItem("gepard_cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    // Check for existing login
    const storedUser = localStorage.getItem("comp_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem("comp_user");
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("gepard_cart", JSON.stringify(cart));
  }, [cart]);

  // Countdown timer effect
  useEffect(() => {
    if (!deadlineEnabled || !orderDeadline) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const deadline = new Date(orderDeadline);
      
      if (now < deadline) {
        // Before deadline - can still order
        setDeadlineStatus("active");
        const diff = deadline - now;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft({ days, hours, minutes, seconds, total: diff });
      } else {
        // After deadline - immediately closed, no grace period
        setDeadlineStatus("closed");
        setTimeLeft(null);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [deadlineEnabled, orderDeadline]);

  const fetchShopSettings = async () => {
    try {
      const response = await axios.get(`${API}/shop/settings`);
      if (response.data) {
        setOrderDeadline(response.data.order_deadline);
        setDeadlineEnabled(response.data.order_deadline_enabled || false);
      }
    } catch (err) {
      console.error("Failed to fetch shop settings");
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (err) {
      console.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const getProductPrice = (product, variations) => {
    if (!product.variation_groups || product.variation_groups.length === 0) {
      return product.base_price;
    }
    
    let maxPrice = product.base_price;
    for (const group of product.variation_groups) {
      const selectedOption = variations[group.name];
      if (selectedOption) {
        const option = group.options.find(o => o.name === selectedOption);
        if (option && option.price > maxPrice) {
          maxPrice = option.price;
        }
      }
    }
    return maxPrice;
  };

  const getVariationImage = (product, variations) => {
    if (!product.variation_groups) return product.image_url;
    
    for (const group of product.variation_groups) {
      const selectedOption = variations[group.name];
      if (selectedOption) {
        const option = group.options.find(o => o.name === selectedOption);
        if (option && option.image_url) {
          return option.image_url;
        }
      }
    }
    return product.image_url;
  };

  const addToCart = (product, variations, customNameValue = "") => {
    let price = getProductPrice(product, variations);
    
    // Add custom name price if provided
    if (customNameValue && product.custom_name_enabled) {
      price += product.custom_name_price || 299;
    }
    
    const image = getVariationImage(product, variations);
    
    const cartItem = {
      product_id: product.id,
      product_name: product.name,
      variation_selections: variations,
      price: price,
      quantity: 1,
      image_url: image,
      custom_name: customNameValue || ""
    };

    const existingIndex = cart.findIndex(
      item => item.product_id === product.id && 
      JSON.stringify(item.variation_selections) === JSON.stringify(variations) &&
      item.custom_name === (customNameValue || "")
    );

    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, cartItem]);
    }

    toast.success("Hozzáadva a kosárhoz!");
    setSelectedProduct(null);
    setSelectedVariations({});
    setCustomName("");
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const getCartTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    if (couponValidation?.valid) {
      return subtotal - couponValidation.discount_amount;
    }
    return subtotal;
  };

  const getCartSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const validateCoupon = async () => {
    if (!checkoutForm.coupon_code.trim()) {
      setCouponValidation(null);
      return;
    }
    
    setValidatingCoupon(true);
    try {
      const subtotal = getCartSubtotal();
      const response = await axios.post(`${API}/coupons/validate?code=${checkoutForm.coupon_code}&order_total=${subtotal}`);
      setCouponValidation(response.data);
      if (response.data.valid) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error);
      }
    } catch (err) {
      setCouponValidation({ valid: false, error: "Hiba a kupon ellenőrzésekor" });
      toast.error("Hiba a kupon ellenőrzésekor");
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleLoginClick = () => {
    setShowAuthModal(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("comp_token");
    localStorage.removeItem("comp_user");
    localStorage.removeItem("comp_admin");
    setUser(null);
    toast.success("Sikeres kijelentkezés!");
  };

  const [pendingCheckout, setPendingCheckout] = useState(false);

  // Prefill the order form from the logged-in account (editable per order, never written back to the profile)
  const openCheckoutForm = async () => {
    const token = localStorage.getItem("comp_token");
    try {
      const res = await axios.get(`${API}/competition/me`, { params: { token } });
      const me = res.data;
      setCheckoutForm((f) => ({ ...f, customer_name: f.customer_name || me.name || "", customer_email: f.customer_email || me.email || "", customer_phone: f.customer_phone || me.phone || "" }));
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        setPendingCheckout(true);
        setShowAuthModal(true);
        return;
      }
    }
    setCheckoutStep(1);
  };

  const handleStartCheckout = () => {
    if (cart.length === 0) {
      toast.error("A kosár üres!");
      return;
    }
    if (!user || !localStorage.getItem("comp_token")) {
      setPendingCheckout(true);
      setShowAuthModal(true);
      toast.info("A rendelés leadásához jelentkezz be vagy regisztrálj. A kosarad megmarad.");
      return;
    }
    openCheckoutForm();
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    if (pendingCheckout) {
      setPendingCheckout(false);
      setShowCart(true);
      openCheckoutForm();
    }
  };

  const handleStepOne = (e) => {
    e.preventDefault();
    if (!checkoutForm.customer_name || !checkoutForm.customer_email || !checkoutForm.customer_phone || !checkoutForm.customer_address) {
      toast.error("Kérlek töltsd ki az összes kötelező mezőt!");
      return;
    }
    setCheckoutStep(2);
  };

  const handleStepTwo = () => {
    setCheckoutStep(3);
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/orders`, {
        ...checkoutForm,
        items: cart,
        coupon_code: couponValidation?.valid ? couponValidation.code : null
      }, { params: { token: localStorage.getItem("comp_token") } });

      if (response.data.success) {
        // If Stripe payment selected, redirect to Stripe checkout
        if (checkoutForm.payment_method === "stripe") {
          try {
            const stripeResponse = await axios.post(`${API}/payments/shop-checkout`, {
              order_id: response.data.order_id,
              origin_url: window.location.origin
            });
            
            if (stripeResponse.data.checkout_url) {
              // Clear cart before redirect
              setCart([]);
              localStorage.removeItem("gepard_cart");
              // Redirect to Stripe
              window.location.href = stripeResponse.data.checkout_url;
              return;
            }
          } catch (stripeErr) {
            toast.error("Hiba a fizetési oldal betöltésekor. Próbáld újra.");
            setSubmitting(false);
            return;
          }
        }
        
        toast.success("Rendelés sikeresen leadva!");
        setCart([]);
        localStorage.removeItem("gepard_cart");
        setShowCart(false);
        setCheckoutStep(0);
        setCouponValidation(null);
        setCheckoutForm({
          customer_name: "",
          customer_email: "",
          customer_phone: "",
          customer_address: "",
          notes: "",
          payment_method: "cash",
          subscribe_newsletter: false,
          coupon_code: ""
        });
        // Navigate to tracking page
        navigate(`/order-tracking/${response.data.order_number}`);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        setPendingCheckout(true);
        setShowAuthModal(true);
        toast.error("A rendelés leadásához be kell jelentkezni.");
      } else {
        toast.error(err.response?.data?.detail || "Hiba történt a rendelés leadásakor.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('hu-HU').format(price) + ' Ft';
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: "Készpénz (előre)",
      transfer: "Banki átutalás (előre)",
      card_in_person: "Terminál (előre)",
      stripe: "Online bankkártya (Stripe)"
    };
    return labels[method] || method;
  };

  return (
    <div className="modern-page min-h-screen">
      <Toaster position="top-center" richColors />
      
      {/* Modern Header */}
      <header className="modern-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Admin Shop button - only visible for admin users */}
            {user?.is_admin && (
              <button
                onClick={() => navigate("/shop-admin")}
                className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                title="Admin Shop"
              >
                <Lock size={16} />
                <span className="hidden sm:inline">Admin Shop</span>
              </button>
            )}
            <a href="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center p-1">
                <img src={LOGO_URL} alt="Gepárd" className="h-7" />
              </div>
              <span className="text-slate-800 font-semibold hidden sm:block">Shop</span>
            </a>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCart(true)}
              className="relative p-2.5 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
            >
              <ShoppingCart size={22} />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </button>
            {user ? (
              <ProfileMenu user={user} onLogout={handleLogout} />
            ) : (
              <button
                onClick={handleLoginClick}
                className="modern-btn modern-btn-primary"
                title="Bejelentkezés"
              >
                <LogIn size={18} />
                <span className="hidden sm:inline">Belépés</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Countdown Banner - only when active */}
      {deadlineEnabled && timeLeft && deadlineStatus === "active" && (
        <div className="fixed top-16 left-0 right-0 z-40 py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB]">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-center">
            <Clock size={20} className="text-[#0A0A0A]" />
            <div className="text-[#0A0A0A]">
              <span className="font-bold">Rendelési határidő: </span>
              <span className="font-mono text-lg">
                {timeLeft.days > 0 && `${timeLeft.days} nap `}
                {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Closed Banner - immediately after deadline */}
      {deadlineStatus === "closed" && deadlineEnabled && (
        <div className="fixed top-16 left-0 right-0 z-40 py-4 px-4 bg-red-900/90">
          <div className="max-w-7xl mx-auto text-center text-white">
            <span className="font-bold">❌ A rendelési időszak lezárult. Jelenleg nem lehet rendelni.</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`pb-12 px-4 ${deadlineEnabled && (timeLeft || deadlineStatus === "closed") ? "pt-36" : "pt-24"}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">Gepárd Shop</h1>
            <p className="text-slate-500">Lovas felszerelések és kiegészítők</p>
          </div>

          {loading ? (
            <div className="text-center text-slate-500">Betöltés...</div>
          ) : products.filter(p => p.is_active !== false).length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Hamarosan érkeznek a termékek!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.filter(p => p.is_active !== false).map((product) => (
                <motion.div
                  key={product.id}
                  className={`modern-card overflow-hidden cursor-pointer group ${
                    product.stock === 0 ? "opacity-70" : ""
                  }`}
                  whileHover={{ y: -4 }}
                  onClick={() => {
                    setSelectedProduct(product);
                    setSelectedVariations({});
                    setSelectedImageIndex(0);
                    setCustomName("");
                  }}
                >
                  <div className="aspect-square bg-slate-100 overflow-hidden relative">
                    {product.image_url ? (
                      <img
                        src={product.image_url.startsWith("/api/") ? `${BACKEND_URL}${product.image_url}` : product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    {product.stock === 0 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">Elfogyott</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-slate-800 font-medium mb-2 line-clamp-2">{product.name}</h3>
                    <p className="text-indigo-600 font-bold">
                      {product.variation_groups?.length > 0 
                        ? `${formatPrice(product.base_price)}-tól`
                        : formatPrice(product.base_price)
                      }
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Product Detail Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            className="modern-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProduct(null)}
          >
            <motion.div
              className="modern-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-slate-600 hover:bg-white shadow-lg"
                >
                  <X size={20} />
                </button>
                
                {/* Main Image */}
                <div className="aspect-square bg-slate-100">
                  {(() => {
                    // Check if a variation with image is selected
                    const selectedVariationImage = getVariationImage(selectedProduct, selectedVariations);
                    
                    // If variation image exists and is selected, show it
                    if (selectedImageIndex === -1 && selectedVariationImage && selectedVariationImage !== selectedProduct.image_url) {
                      return (
                        <img
                          src={selectedVariationImage.startsWith("/api/") ? `${BACKEND_URL}${selectedVariationImage}` : selectedVariationImage}
                          alt={selectedProduct.name}
                          className="w-full h-full object-contain"
                        />
                      );
                    }
                    
                    // Otherwise show from gallery
                    const allImages = [selectedProduct.image_url, ...(selectedProduct.gallery_images || [])].filter(Boolean);
                    const currentImage = selectedImageIndex >= 0 ? (allImages[selectedImageIndex] || selectedProduct.image_url) : selectedProduct.image_url;
                    return currentImage ? (
                      <img
                        src={currentImage.startsWith("/api/") ? `${BACKEND_URL}${currentImage}` : currentImage}
                        alt={selectedProduct.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-slate-300" />
                      </div>
                    );
                  })()}
                </div>
                
                {/* Image Gallery Thumbnails */}
                {(() => {
                  const allImages = [selectedProduct.image_url, ...(selectedProduct.gallery_images || [])].filter(Boolean);
                  return allImages.length > 1 && (
                    <div className="flex gap-2 p-3 bg-slate-50 overflow-x-auto">
                      {allImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedImageIndex(index);
                          }}
                          className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
                            selectedImageIndex === index
                              ? "border-indigo-500"
                              : "border-transparent hover:border-indigo-300"
                          }`}
                        >
                          <img
                            src={img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img}
                            alt={`Kép ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              <div className="p-6">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">{selectedProduct.name}</h2>
                <p className="text-2xl text-indigo-600 font-bold mb-4">
                  {formatPrice(getProductPrice(selectedProduct, selectedVariations))}
                </p>
                
                {selectedProduct.description && (
                  <p className="text-slate-600 mb-4">{selectedProduct.description}</p>
                )}
                
                {selectedProduct.washing_instructions && (
                  <div className="mb-4 p-3 bg-slate-100 rounded-lg">
                    <p className="text-slate-600 text-sm">
                      <strong className="text-slate-800">Mosási útmutató:</strong> {selectedProduct.washing_instructions}
                    </p>
                  </div>
                )}

                {selectedProduct.variation_groups?.map((group) => (
                  <div key={group.id} className="mb-4">
                    <label className="text-slate-600 text-sm mb-2 block">{group.name}</label>
                    <div className="flex flex-wrap gap-2">
                      {group.options.map((option) => {
                        const hasStock = option.stock !== null && option.stock !== undefined;
                        const isOutOfStock = hasStock && option.stock === 0;
                        return (
                          <button
                            key={option.id}
                            onClick={() => {
                              if (!isOutOfStock) {
                                setSelectedVariations({
                                  ...selectedVariations,
                                  [group.name]: option.name
                                });
                                // If this variation has an image, show it as main image
                                if (option.image_url) {
                                  setSelectedImageIndex(-1); // -1 means variation image
                                }
                              }
                            }}
                            disabled={isOutOfStock}
                            className={`px-3 py-2 rounded-lg border transition-colors relative ${
                              isOutOfStock
                                ? "border-slate-200 text-slate-300 cursor-not-allowed"
                                : selectedVariations[group.name] === option.name
                                ? "bg-indigo-600 border-indigo-600 text-white"
                                : "border-slate-300 text-slate-700 hover:border-indigo-400"
                            }`}
                          >
                            <span>{option.name}</span>
                            {hasStock && (
                              <span className={`ml-1 text-xs ${
                                isOutOfStock 
                                  ? "text-red-400" 
                                  : option.stock <= 3 
                                  ? "text-amber-500" 
                                  : "text-green-600"
                              }`}>
                                ({isOutOfStock ? "Elfogyott" : `${option.stock} db`})
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Size Chart Section - Below description */}
                {selectedProduct.size_chart_images?.length > 0 && (
                  <div className="mb-4 p-4 bg-slate-100 rounded-lg border border-slate-200">
                    <p className="text-indigo-600 text-sm font-medium mb-3">📏 Mérettáblázat</p>
                    <div className="space-y-3">
                      {selectedProduct.size_chart_images.map((img, index) => (
                        <img
                          key={index}
                          src={img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img}
                          alt={`Mérettáblázat ${index + 1}`}
                          className="w-full rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Name Option */}
                {selectedProduct.custom_name_enabled && (
                  <div className="mb-4 p-4 bg-slate-100 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-slate-800 font-medium">✏️ Egyedi név</label>
                      <span className="text-indigo-600 text-sm">+{formatPrice(selectedProduct.custom_name_price || 299)}</span>
                    </div>
                    <Input
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value.slice(0, selectedProduct.custom_name_max_length || 15))}
                      className="bg-white border-slate-300 text-slate-800"
                      placeholder="Add meg az egyedi nevet (opcionális)"
                      maxLength={selectedProduct.custom_name_max_length || 15}
                    />
                    <p className="text-slate-500 text-xs mt-1">
                      {customName.length}/{selectedProduct.custom_name_max_length || 15} karakter
                    </p>
                  </div>
                )}

                {(() => {
                  // Check if any selected variation is out of stock
                  let isVariationOutOfStock = false;
                  if (selectedProduct.variation_groups?.length > 0) {
                    for (const group of selectedProduct.variation_groups) {
                      const selectedOptionName = selectedVariations[group.name];
                      if (selectedOptionName) {
                        const option = group.options.find(o => o.name === selectedOptionName);
                        if (option && option.stock !== null && option.stock !== undefined && option.stock === 0) {
                          isVariationOutOfStock = true;
                          break;
                        }
                      }
                    }
                  }

                  const isProductOutOfStock = selectedProduct.stock === 0;
                  const isOutOfStock = isProductOutOfStock || isVariationOutOfStock;
                  const notAllVariationsSelected = selectedProduct.variation_groups?.length > 0 &&
                    Object.keys(selectedVariations).length !== selectedProduct.variation_groups.length;

                  // Calculate total price including custom name
                  let totalPrice = getProductPrice(selectedProduct, selectedVariations);
                  if (customName && selectedProduct.custom_name_enabled) {
                    totalPrice += selectedProduct.custom_name_price || 299;
                  }

                  if (isOutOfStock) {
                    return (
                      <div className="w-full bg-red-100 border border-red-400 text-red-600 text-center py-3 rounded-lg font-medium">
                        Elfogyott - Jelenleg nem rendelhető
                      </div>
                    );
                  }

                  return (
                    <>
                      <Button
                        onClick={() => addToCart(selectedProduct, selectedVariations, customName)}
                        className="w-full bg-indigo-600 text-white hover:bg-indigo-700 font-medium py-3"
                        disabled={notAllVariationsSelected}
                      >
                        Kosárba - {formatPrice(totalPrice)}
                      </Button>
                      
                      {notAllVariationsSelected && (
                        <p className="text-slate-500 text-sm text-center mt-2">
                          Kérlek válassz minden opcióból
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Sidebar */}
      <AnimatePresence>
        {showCart && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setShowCart(false);
                setCheckoutStep(0);
              }}
            />
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white z-50 flex flex-col shadow-xl"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween" }}
            >
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">
                  {checkoutStep === 0 && "Kosár"}
                  {checkoutStep === 1 && "Szállítási adatok"}
                  {checkoutStep === 2 && "Fizetési mód"}
                  {checkoutStep === 3 && "Összegzés"}
                </h2>
                <button
                  onClick={() => {
                    setShowCart(false);
                    setCheckoutStep(0);
                  }}
                  className="text-slate-500 hover:text-slate-800"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Step 0: Cart Items */}
              {checkoutStep === 0 && (
                <>
                  <div className="flex-1 overflow-y-auto p-4">
                    {cart.length === 0 ? (
                      <div className="text-center py-12">
                        <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">A kosár üres</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {cart.map((item, index) => (
                          <div key={index} className="flex gap-4 bg-slate-50 p-3 rounded-lg">
                            <div className="w-20 h-20 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                              {item.image_url && (
                                <img
                                  src={item.image_url.startsWith("/api/") ? `${BACKEND_URL}${item.image_url}` : item.image_url}
                                  alt={item.product_name}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-slate-800 font-medium text-sm truncate">{item.product_name}</h4>
                              {Object.entries(item.variation_selections).length > 0 && (
                                <p className="text-slate-500 text-xs">
                                  {Object.entries(item.variation_selections).map(([k, v]) => `${k}: ${v}`).join(", ")}
                                </p>
                              )}
                              {item.custom_name && (
                                <p className="text-indigo-600 text-xs">
                                  ✏️ Egyedi név: {item.custom_name}
                                </p>
                              )}
                              <p className="text-indigo-600 font-bold text-sm mt-1">{formatPrice(item.price)}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={() => updateCartQuantity(index, -1)}
                                  className="w-6 h-6 bg-slate-200 rounded flex items-center justify-center text-slate-600 hover:bg-slate-300"
                                >
                                  <Minus size={14} />
                                </button>
                                <span className="text-slate-800 text-sm w-6 text-center">{item.quantity}</span>
                                <button
                                  onClick={() => updateCartQuantity(index, 1)}
                                  className="w-6 h-6 bg-slate-200 rounded flex items-center justify-center text-slate-600 hover:bg-slate-300"
                                >
                                  <Plus size={14} />
                                </button>
                                <button
                                  onClick={() => removeFromCart(index)}
                                  className="ml-auto text-red-500 hover:text-red-400"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {cart.length > 0 && (
                    <div className="p-4 border-t border-slate-200">
                      <div className="flex justify-between text-lg font-bold text-slate-800 mb-4">
                        <span>Összesen:</span>
                        <span className="text-indigo-600">{formatPrice(getCartTotal())}</span>
                      </div>
                      <Button
                        onClick={handleStartCheckout}
                        data-testid="start-checkout-btn"
                        className="w-full bg-indigo-600 text-white hover:bg-indigo-700 font-medium py-3"
                      >
                        {user ? "Rendelés véglegesítése" : "Bejelentkezés a rendeléshez"}
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Step 1: Customer Details */}
              {checkoutStep === 1 && (
                <div className="flex-1 overflow-y-auto p-4">
                  <button
                    onClick={() => setCheckoutStep(0)}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-4"
                  >
                    <ArrowLeft size={20} />
                    Vissza a kosárhoz
                  </button>
                  
                  <form onSubmit={handleStepOne} className="space-y-4">
                    <p className="rounded-xl bg-orange-50 px-3 py-2 text-xs text-slate-700" data-testid="checkout-prefill-note">Az adatokat a fiókodból töltöttük ki. Ehhez a rendeléshez szabadon módosíthatod, a profilodat nem írja felül.</p>
                    <div>
                      <label className="text-slate-600 text-sm mb-1 block">Név *</label>
                      <Input
                        required
                        data-testid="checkout-name-input"
                        value={checkoutForm.customer_name}
                        onChange={(e) => setCheckoutForm({...checkoutForm, customer_name: e.target.value})}
                        className="bg-white border-slate-300 text-slate-800"
                        placeholder="Teljes név"
                      />
                    </div>
                    <div>
                      <label className="text-slate-600 text-sm mb-1 block">Telefon *</label>
                      <Input
                        type="tel"
                        required
                        data-testid="checkout-phone-input"
                        value={checkoutForm.customer_phone}
                        onChange={(e) => setCheckoutForm({...checkoutForm, customer_phone: e.target.value})}
                        className="bg-white border-slate-300 text-slate-800"
                        placeholder="+36 30 123 4567"
                      />
                    </div>
                    <div>
                      <label className="text-slate-600 text-sm mb-1 block">Email *</label>
                      <Input
                        type="email"
                        required
                        data-testid="checkout-email-input"
                        value={checkoutForm.customer_email}
                        onChange={(e) => setCheckoutForm({...checkoutForm, customer_email: e.target.value})}
                        className="bg-white border-slate-300 text-slate-800"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="text-slate-600 text-sm mb-1 block">Szállítási cím *</label>
                      <textarea
                        required
                        value={checkoutForm.customer_address}
                        onChange={(e) => setCheckoutForm({...checkoutForm, customer_address: e.target.value})}
                        rows={3}
                        className="w-full bg-white border border-slate-300 rounded-md text-slate-800 p-3"
                        placeholder="Irányítószám, város, utca, házszám"
                      />
                    </div>
                    <div>
                      <label className="text-slate-600 text-sm mb-1 block">Megjegyzés (opcionális)</label>
                      <textarea
                        value={checkoutForm.notes}
                        onChange={(e) => setCheckoutForm({...checkoutForm, notes: e.target.value})}
                        rows={2}
                        className="w-full bg-white border border-slate-300 rounded-md text-slate-800 p-3"
                        placeholder="Egyéb megjegyzés..."
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-indigo-600 text-white hover:bg-indigo-700 font-medium py-3"
                    >
                      Tovább
                    </Button>
                  </form>
                </div>
              )}

              {/* Step 2: Payment Method */}
              {checkoutStep === 2 && (
                <div className="flex-1 overflow-y-auto p-4">
                  <button
                    onClick={() => setCheckoutStep(1)}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-4"
                  >
                    <ArrowLeft size={20} />
                    Vissza
                  </button>
                  
                  <h3 className="text-slate-800 font-medium mb-4">Válassz fizetési módot:</h3>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => setCheckoutForm({...checkoutForm, payment_method: "transfer"})}
                      className={`w-full p-4 rounded-lg border flex items-center gap-4 transition-colors ${
                        checkoutForm.payment_method === "transfer"
                          ? "bg-indigo-50 border-indigo-500"
                          : "bg-slate-50 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        checkoutForm.payment_method === "transfer" ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-600"
                      }`}>
                        <Building2 size={20} />
                      </div>
                      <div className="text-left">
                        <p className="text-slate-800 font-medium">Banki átutalás (előre)</p>
                        <p className="text-slate-500 text-sm">Előre utalás, utána készítjük el a rendelést</p>
                      </div>
                      {checkoutForm.payment_method === "transfer" && (
                        <Check className="ml-auto text-indigo-600" size={20} />
                      )}
                    </button>

                    <button
                      onClick={() => setCheckoutForm({...checkoutForm, payment_method: "cash"})}
                      className={`w-full p-4 rounded-lg border flex items-center gap-4 transition-colors ${
                        checkoutForm.payment_method === "cash"
                          ? "bg-indigo-50 border-indigo-500"
                          : "bg-slate-50 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        checkoutForm.payment_method === "cash" ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-600"
                      }`}>
                        <Banknote size={20} />
                      </div>
                      <div className="text-left">
                        <p className="text-slate-800 font-medium">Készpénz (előre)</p>
                        <p className="text-slate-500 text-sm">Előre fizetsz készpénzzel személyesen</p>
                      </div>
                      {checkoutForm.payment_method === "cash" && (
                        <Check className="ml-auto text-indigo-600" size={20} />
                      )}
                    </button>

                    <button
                      onClick={() => setCheckoutForm({...checkoutForm, payment_method: "card_in_person"})}
                      className={`w-full p-4 rounded-lg border flex items-center gap-4 transition-colors ${
                        checkoutForm.payment_method === "card_in_person"
                          ? "bg-indigo-50 border-indigo-500"
                          : "bg-slate-50 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        checkoutForm.payment_method === "card_in_person" ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-600"
                      }`}>
                        <CreditCard size={20} />
                      </div>
                      <div className="text-left">
                        <p className="text-slate-800 font-medium">Terminál (előre)</p>
                        <p className="text-slate-500 text-sm">Előre fizetsz terminálnál személyesen</p>
                      </div>
                      {checkoutForm.payment_method === "card_in_person" && (
                        <Check className="ml-auto text-indigo-600" size={20} />
                      )}
                    </button>

                    <div
                      className="w-full p-4 rounded-lg border flex items-center gap-4 bg-slate-100 border-slate-200 cursor-not-allowed opacity-60"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-300 text-slate-500">
                        <Globe size={20} />
                      </div>
                      <div className="text-left">
                        <p className="text-slate-500 font-medium">Online bankkártya</p>
                        <p className="text-slate-400 text-sm">Jelenleg nem elérhető</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Show bank transfer details if selected */}
                  {checkoutForm.payment_method === "transfer" && (
                    <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <p className="text-indigo-700 font-medium mb-2">Utalási adatok:</p>
                      <div className="text-slate-800 text-sm space-y-1">
                        <p><span className="text-slate-500">Számlatulajdonos:</span> Haklik Róbert Dávid</p>
                        <p><span className="text-slate-500">Bank:</span> OTP Bank</p>
                        <p><span className="text-slate-500">Számlaszám:</span> <span className="font-mono">11773339-02063041</span></p>
                        <p><span className="text-slate-500">Közlemény:</span> Rendelés azonosító (emailben kapod)</p>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleStepTwo}
                    className="w-full bg-indigo-600 text-white hover:bg-indigo-700 font-medium py-3 mt-6"
                  >
                    Tovább
                  </Button>
                </div>
              )}

              {/* Step 3: Summary & Confirm */}
              {checkoutStep === 3 && (
                <div className="flex-1 overflow-y-auto p-4">
                  <button
                    onClick={() => setCheckoutStep(2)}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-4"
                  >
                    <ArrowLeft size={20} />
                    Vissza
                  </button>
                  
                  <div className="space-y-4">
                    {/* Coupon Code */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-slate-800 font-medium mb-3">Kuponkód</h4>
                      <div className="flex gap-2">
                        <Input
                          value={checkoutForm.coupon_code}
                          onChange={(e) => {
                            setCheckoutForm({...checkoutForm, coupon_code: e.target.value.toUpperCase()});
                            setCouponValidation(null);
                          }}
                          className="bg-white border-slate-300 text-slate-800 flex-1"
                          placeholder="Add meg a kuponkódot"
                        />
                        <Button
                          type="button"
                          onClick={validateCoupon}
                          disabled={validatingCoupon || !checkoutForm.coupon_code.trim()}
                          className="bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                          {validatingCoupon ? "..." : "Beváltás"}
                        </Button>
                      </div>
                      {couponValidation && (
                        <p className={`text-sm mt-2 ${couponValidation.valid ? "text-green-600" : "text-red-500"}`}>
                          {couponValidation.valid ? couponValidation.message : couponValidation.error}
                        </p>
                      )}
                    </div>

                    {/* Order Summary */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-slate-800 font-medium mb-3">Rendelés összegzés</h4>
                      {cart.map((item, index) => (
                        <div key={index} className="text-sm mb-2">
                          <div className="flex justify-between">
                            <span className="text-slate-600">
                              {item.product_name} x{item.quantity}
                              {Object.keys(item.variation_selections).length > 0 && (
                                <span className="text-slate-500 ml-1">
                                  ({Object.values(item.variation_selections).join(", ")})
                                </span>
                              )}
                            </span>
                            <span className="text-slate-800">{formatPrice(item.price * item.quantity)}</span>
                          </div>
                          {item.custom_name && (
                            <p className="text-indigo-600 text-xs ml-2">✏️ Egyedi név: {item.custom_name}</p>
                          )}
                        </div>
                      ))}
                      
                      {couponValidation?.valid && (
                        <>
                          <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between text-sm">
                            <span className="text-slate-600">Részösszeg:</span>
                            <span className="text-slate-800">{formatPrice(getCartSubtotal())}</span>
                          </div>
                          <div className="flex justify-between text-sm text-green-600 mt-1">
                            <span>Kupon kedvezmény ({couponValidation.code}):</span>
                            <span>-{formatPrice(couponValidation.discount_amount)}</span>
                          </div>
                        </>
                      )}
                      
                      <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between font-bold">
                        <span className="text-slate-800">Összesen:</span>
                        <span className="text-indigo-600">{formatPrice(getCartTotal())}</span>
                      </div>
                    </div>

                    {/* Customer Details */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-slate-800 font-medium mb-2">Szállítási adatok</h4>
                      <p className="text-slate-600 text-sm">{checkoutForm.customer_name}</p>
                      <p className="text-slate-600 text-sm">{checkoutForm.customer_phone}</p>
                      <p className="text-slate-600 text-sm">{checkoutForm.customer_email}</p>
                      <p className="text-slate-600 text-sm">{checkoutForm.customer_address}</p>
                    </div>

                    {/* Payment Method */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-slate-800 font-medium mb-2">Fizetési mód</h4>
                      <p className="text-indigo-600">{getPaymentMethodLabel(checkoutForm.payment_method)}</p>
                      {checkoutForm.payment_method === "transfer" && (
                        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm">
                          <p className="text-indigo-700 font-medium mb-2">Utalási adatok:</p>
                          <p className="text-slate-600"><span className="text-slate-500">Számlatulajdonos:</span> Haklik Róbert Dávid</p>
                          <p className="text-slate-600"><span className="text-slate-500">Bank:</span> OTP Bank</p>
                          <p className="text-slate-600"><span className="text-slate-500">Számlaszám:</span> <span className="font-mono">11773339-02063041</span></p>
                          <p className="text-slate-600"><span className="text-slate-500">Közlemény:</span> Rendelésszám (emailben)</p>
                          <p className="text-slate-500 text-xs mt-2">A rendelés a befizetés beérkezése után kerül feldolgozásra.</p>
                        </div>
                      )}
                    </div>

                    {/* Newsletter */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checkoutForm.subscribe_newsletter}
                          onChange={(e) => setCheckoutForm({...checkoutForm, subscribe_newsletter: e.target.checked})}
                          className="mt-1 w-4 h-4 rounded border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <p className="text-slate-800 text-sm">Feliratkozom a hírlevélre</p>
                          <p className="text-slate-500 text-xs">Értesülj elsőként akcióinkról és újdonságainkról</p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleFinalSubmit}
                    disabled={submitting}
                    className="w-full bg-indigo-600 text-white hover:bg-indigo-700 font-medium py-3 mt-6"
                  >
                    {submitting ? "Feldolgozás..." : "Rendelés véglegesítése"}
                  </Button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Auth Modal - Unified component */}
      <AuthModal 
        show={showAuthModal}
        onClose={() => { setShowAuthModal(false); setPendingCheckout(false); }}
        onLoginSuccess={handleAuthSuccess}
      />
    </div>
  );
}
